import { useCallback, useContext, useMemo, useRef } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { ResponseContext } from "@swiggy-private/portal-dineout-context";
import type { AppliedFilters } from "@swiggy-private/portal-dineout-context";
import { IPLWidgetControllerCards } from "utils/types";
import { MXN_ACTION_TYPE } from "../constants/menu";
import {
    resetInlineFacetSelections,
    filterCardsByAppliedFilters,
    buildFacetSelectionMap,
} from "../utils/filterHelpers";
import { filterByParentType, generateMenuCategoryWidgetData, filterByCategoryId } from "../utils/menu";

const useFilters = () => {
    const { state } = useContext(ResponseContext);

    const appliedFiltersRef = useRef<AppliedFilters>({
        singleSelect: {},
        multiSelect: {},
    });

    const facetIdToSelection = useMemo(() => {
        return buildFacetSelectionMap(state?.menu?.initialData || []);
    }, [state?.menu?.initialData]);

    const clearFilters = useCallback(() => {
        appliedFiltersRef.current = { singleSelect: {}, multiSelect: {} };

        const baseParent = filterByParentType(state?.menu?.initialData || [], state?.menu?.parentType || "");
        const categoryWidget = generateMenuCategoryWidgetData(baseParent, state?.menu?.categoryId || "");
        const afterCategory = filterByCategoryId(categoryWidget, state?.menu?.categoryId || "");
        const updated = resetInlineFacetSelections(afterCategory);

        return { filteredData: updated, appliedFilters: appliedFiltersRef.current };
    }, [state?.menu?.initialData, state?.menu?.parentType, state?.menu?.categoryId]);

    const updateFilters = useCallback(
        (extraParams?: Record<string, unknown>) => {
            // Snapshot-based update using selectedFilters
            const selectedFilters = extraParams?.selectedFilters as Record<string, string[]> | undefined;
            if (!selectedFilters) return;

            const nextSingle: Record<string, string> = {};
            const nextMulti: Record<string, string[]> = {};

            Object.entries(selectedFilters).forEach(([facetId, values]) => {
                const selectionType = facetIdToSelection.get(facetId) || "";
                if (selectionType === "SELECT_TYPE_SINGLESELECT") {
                    const first = Array.isArray(values) && values.length > 0 ? values[0] : "";
                    if (first) nextSingle[facetId] = first;
                } else if (selectionType === "SELECT_TYPE_MULTISELECT") {
                    if (Array.isArray(values) && values.length > 0) {
                        nextMulti[facetId] = values;
                    }
                }
            });

            appliedFiltersRef.current = { singleSelect: nextSingle, multiSelect: nextMulti };
        },
        [facetIdToSelection],
    );

    const applyFilters = useCallback(
        (extraParams?: Record<string, unknown>, existingFilterResults?: IPLWidgetControllerCards[]) => {
            const action = (extraParams?.action as string) || "";
            const menuCards = isEmpty(existingFilterResults) ? state?.menu?.initialData : existingFilterResults;

            if (action === MXN_ACTION_TYPE.CLEAR_FILTERS) {
                return clearFilters();
            }

            updateFilters(extraParams);

            // Rebuild filtered data in the same order as existing flow:
            // 1) Filter by parentType
            const baseParent = filterByParentType(menuCards, state?.menu?.parentType || "");
            // 3) Apply applied filters (AND across types, OR within values) instead of veg filter
            const afterApplied = filterCardsByAppliedFilters(baseParent, appliedFiltersRef.current);
            // 2) Generate Category widget structure
            const categoryWidget = generateMenuCategoryWidgetData(afterApplied, state?.menu?.categoryId || "");

            // 4) Apply categoryId selection
            const afterCategory = filterByCategoryId(categoryWidget, state?.menu?.categoryId || "");

            return { filteredData: afterCategory, appliedFilters: appliedFiltersRef.current };
        },
        [state?.menu?.initialData, state?.menu?.parentType, state?.menu?.categoryId, updateFilters, clearFilters],
    );

    return { applyFilters, clearFilters };
};

export default useFilters;
